import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import type { PublicNavGroupProps } from '@/features/common/types/PublicNavGroupProps';
import '@/features/common/layout/site-nav.css';

/**
 * A primary-navigation item that opens a list of links (MUD Components/header.html,
 * `nav__item--has-dropdown`).
 *
 * A disclosure — a button with aria-expanded over a plain list of links — not the `role="menu"`
 * MUD's markup carries: a menu promises arrow-key navigation and application semantics, and a
 * list of pages to go to is navigation, which a screen reader already knows how to read (WAI-ARIA
 * APG, "Disclosure Navigation Menu").
 *
 * Escape is handled on the group, not on the document, and stops there: inside the phone drawer
 * the header listens for Escape too, and one press would otherwise close the list and the drawer
 * together and leave focus on the drawer's toggle.
 */
export function PublicNavGroup({ label, links }: PublicNavGroupProps) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !open) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    toggleRef.current?.focus();
  };

  // The toggle says where the reader is when the page is one of its links, as a primary link would.
  const current = links.some((link) => link.to === pathname);

  return (
    <div className="site-nav__group" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={toggleRef}
        type="button"
        className={'site-nav__group-toggle' + (current ? ' active' : '')}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <Icon name="chevron-bottom" className="site-nav__group-chevron" />
      </button>
      {open && (
        <ul className="site-nav__group-list" id={listId}>
          {links.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                end
                lang={link.lang}
                className={({ isActive }) =>
                  'site-nav__group-link' + (isActive ? ' site-nav__group-link--current' : '')
                }
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
